'use client';

import { useState, useEffect } from 'react';
import { API_BASE } from '@/lib/env';

interface GeolocationResult {
  city: string | null;
  province: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  isLoading: boolean;
  error: string | null;
}

const CITY_COORDINATES: Record<string, { lat: number; lng: number }> = {
  'Madrid': { lat: 40.4168, lng: -3.7038 },
  'Barcelona': { lat: 41.3851, lng: 2.1734 },
  'Valencia': { lat: 39.4699, lng: -0.3763 },
  'Sevilla': { lat: 37.3891, lng: -5.9845 },
  'Zaragoza': { lat: 41.6488, lng: -0.8891 },
  'Málaga': { lat: 36.7213, lng: -4.4214 },
  'Murcia': { lat: 37.9922, lng: -1.1307 },
  'Palma': { lat: 39.5696, lng: 2.6502 },
  'Bilbao': { lat: 43.263, lng: -2.935 },
  'Alicante': { lat: 38.3452, lng: -0.481 },
  'Córdoba': { lat: 37.8882, lng: -4.7794 },
  'Valladolid': { lat: 41.6523, lng: -4.7245 },
  'Vigo': { lat: 42.2406, lng: -8.7207 },
  'Gijón': { lat: 43.5322, lng: -5.6611 },
  'Granada': { lat: 37.1773, lng: -3.5986 },
};

const CACHE_KEY = 'jamsom_geo_cache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24h

interface CachedGeo {
  city: string | null;
  province: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  timestamp: number;
}

function getCachedGeo(): CachedGeo | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const data: CachedGeo = JSON.parse(cached);
    if (Date.now() - data.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCachedGeo(geo: Omit<CachedGeo, 'timestamp'>) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...geo, timestamp: Date.now() }));
  } catch {
    // localStorage not available
  }
}

export function useGeolocation(): GeolocationResult {
  const [result, setResult] = useState<GeolocationResult>({
    city: null, province: null, country: null,
    latitude: null, longitude: null,
    isLoading: true, error: null,
  });

  useEffect(() => {
    async function detectLocation() {
      const cached = getCachedGeo();
      if (cached) {
        setResult({
          city: cached.city, province: cached.province, country: cached.country,
          latitude: cached.latitude, longitude: cached.longitude,
          isLoading: false, error: null,
        });
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/geolocation`);
        if (response.ok) {
          const data = await response.json();
          if (data.city) {
            const geo = {
              city: data.city, province: data.province, country: data.country,
              latitude: data.latitude, longitude: data.longitude,
            };
            setCachedGeo(geo);
            setResult({ ...geo, isLoading: false, error: null });
            return;
          }
        }
      } catch (error) {
        console.warn('Error fetching geolocation:', error);
      }

      // Fallback to Madrid
      const defaultGeo = {
        city: 'Madrid', province: 'Madrid', country: 'España',
        latitude: CITY_COORDINATES['Madrid'].lat,
        longitude: CITY_COORDINATES['Madrid'].lng,
      };
      setCachedGeo(defaultGeo);
      setResult({ ...defaultGeo, isLoading: false, error: null });
    }

    detectLocation();
  }, []);

  return result;
}

export function getCityCoordinates(city: string): { lat: number; lng: number } | null {
  if (CITY_COORDINATES[city]) return CITY_COORDINATES[city];
  const normalized = city.toLowerCase();
  for (const [name, coords] of Object.entries(CITY_COORDINATES)) {
    if (name.toLowerCase().includes(normalized) || normalized.includes(name.toLowerCase())) {
      return coords;
    }
  }
  return null;
}

export function useUserCity(): { city: string | null; isLoading: boolean } {
  const { city, isLoading } = useGeolocation();
  return { city, isLoading };
}
